import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatPrice, formatDate } from '../lib/utils';
import { resolveImageUrl } from '../lib/imageUrl';

const API_URL = '';

const STATUS_LABELS = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  processing: 'En préparation',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée'
};

const STATUS_COLORS = {
  pending: 'text-yellow-600',
  confirmed: 'text-blue-600',
  processing: 'text-purple-600',
  shipped: 'text-orange-600',
  delivered: 'text-green-600',
  cancelled: 'text-red-600'
};

export default function OrdersPage() {
  const { user, login } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/orders`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setOrders(data.orders || []);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center">
          <Package size={48} className="text-[#E0DFDD] mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-4">Mes Commandes</h1>
          <p className="text-[#6B6A68] mb-6">Connecte-toi pour voir tes commandes</p>
          <button 
            onClick={login}
            className="btn-primary px-6 py-3 text-sm font-medium"
            data-testid="login-btn"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="orders-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="breadcrumb flex items-center gap-2 text-sm">
          <Link to="/" className="hover:text-[#1A1514]">Accueil</Link>
          <span>/</span>
          <span className="text-[#1A1514]">Mes Commandes</span>
        </nav>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-serif text-3xl mb-8">Mes Commandes</h1>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white p-6 animate-pulse">
                <div className="h-4 bg-[#F5F4F2] w-1/3 mb-4" />
                <div className="h-4 bg-[#F5F4F2] w-1/2" />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package size={48} className="text-[#E0DFDD] mx-auto mb-4" />
            <p className="text-[#6B6A68] mb-6">Tu n'as pas encore de commandes</p>
            <Link to="/shop" className="btn-primary px-6 py-3 inline-block text-sm font-medium">
              Découvrir la Collection
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.order_id} className="bg-white p-6" data-testid={`order-${order.order_id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div>
                    <p className="font-medium">Commande #{order.order_id}</p>
                    <p className="text-sm text-[#6B6A68]">{formatDate(order.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-sm font-medium ${STATUS_COLORS[order.order_status]}`}>
                      {STATUS_LABELS[order.order_status]}
                    </span>
                    <span className="font-serif text-lg">{formatPrice(order.total)}</span>
                  </div>
                </div>

                {/* Order Items Preview */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex -space-x-2">
                    {order.items.slice(0, 3).map((item, idx) => (
                      <div 
                        key={idx}
                        className="w-12 h-16 bg-[#F5F4F2] border-2 border-white overflow-hidden"
                      >
                        {item.image_url && (
                          <img 
                            src={resolveImageUrl(item.image_url)} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <div className="w-12 h-16 bg-[#F5F4F2] border-2 border-white flex items-center justify-center">
                        <span className="text-xs text-[#6B6A68]">+{order.items.length - 3}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-[#6B6A68]">
                    {order.items.length} article{order.items.length > 1 ? 's' : ''}
                  </div>
                </div>

                {/* View Details Link */}
                <Link 
                  to={`/orders/${order.order_id}`}
                  className="flex items-center gap-2 text-sm text-[#D4AF37] hover:underline"
                  data-testid={`view-order-${order.order_id}`}
                >
                  Voir les détails
                  <ChevronRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
